import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Store, ChevronDown, ChevronRight, X, FileText } from "lucide-react";

const eur = (n) => (Number(n) || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const MESI = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const meseLabel = (ym) => { const [y, m] = (ym || "").split("-"); return m ? `${MESI[parseInt(m)]} ${y}` : ym; };

export default function Rivenditori() {
  const [data, setData] = useState({ partners: [], totale_imponibile: 0, totale_iva: 0, totale: 0 });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [f, setF] = useState({ dal: "", al: "" });

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (f.dal) params.dal = f.dal;
    if (f.al) params.al = f.al;
    api.get("/rivenditori", { params }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const hasF = f.dal || f.al;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="rivenditori-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><Store className="h-7 w-7 text-violet-600" /> Da fatturare ai Rivenditori</h1>
        <p className="text-sm text-slate-500 mt-1">Importo che l'Associazione ICS deve fatturare ai partner rivenditori (quota % del prezzo + IVA) sui corsi venduti, per mese.</p>
      </div>

      <Card className="border-slate-200 shadow-sm p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div><Label className="text-xs">Dal</Label><Input type="date" value={f.dal} onChange={(e) => setF((s) => ({ ...s, dal: e.target.value }))} className="mt-1.5 bg-white" data-testid="riv-dal" /></div>
          <div><Label className="text-xs">Al</Label><Input type="date" value={f.al} onChange={(e) => setF((s) => ({ ...s, al: e.target.value }))} className="mt-1.5 bg-white" data-testid="riv-al" /></div>
          {hasF && <Button variant="ghost" size="sm" onClick={() => setF({ dal: "", al: "" })} className="text-slate-500 justify-self-start" data-testid="riv-reset"><X className="h-3.5 w-3.5 mr-1" /> Azzera</Button>}
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4 border-slate-200"><div className="text-xs text-slate-500 uppercase tracking-wider">Imponibile totale</div><div className="text-2xl font-bold text-slate-800 mt-1" data-testid="riv-tot-imponibile">{eur(data.totale_imponibile)}</div></Card>
        <Card className="p-4 border-slate-200"><div className="text-xs text-slate-500 uppercase tracking-wider">IVA totale</div><div className="text-2xl font-bold text-slate-800 mt-1" data-testid="riv-tot-iva">{eur(data.totale_iva)}</div></Card>
        <Card className="p-4 border-violet-200 bg-violet-50/40"><div className="text-xs text-violet-600 uppercase tracking-wider">Totale da fatturare</div><div className="text-2xl font-bold text-violet-700 mt-1" data-testid="riv-tot">{eur(data.totale)}</div></Card>
      </div>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm text-slate-500">{loading ? "Caricamento…" : `${data.partners.length} rivenditori`}</div>
        <div className="divide-y divide-slate-100">
          {data.partners.map((p) => (
            <div key={p.partner_id} data-testid={`riv-partner-${p.partner_id}`}>
              <button onClick={() => toggle(p.partner_id)} className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 text-left">
                <div className="flex items-center gap-2 min-w-0">
                  {expanded[p.partner_id] ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
                  <span className="font-medium text-slate-800 truncate">{p.nome}</span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 shrink-0">{p.rivendita_percent}% + IVA</span>
                  <span className="text-xs text-slate-400 shrink-0">· {p.count} corsi</span>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-bold text-violet-700">{eur(p.totale)}</div>
                  <div className="text-[11px] text-slate-400">imp. {eur(p.imponibile)} + iva {eur(p.iva)}</div>
                </div>
              </button>
              {expanded[p.partner_id] && (
                <div className="bg-slate-50/60 px-4 py-2">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-slate-400 text-xs"><th className="py-1 font-medium">Mese</th><th className="py-1 font-medium text-right">Corsi</th><th className="py-1 font-medium text-right">Imponibile</th><th className="py-1 font-medium text-right">IVA</th><th className="py-1 font-medium text-right">Totale</th></tr></thead>
                    <tbody>
                      {p.mesi.map((m) => (
                        <tr key={m.mese} className="border-t border-slate-100">
                          <td className="py-1.5 text-slate-700 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-slate-300" />{meseLabel(m.mese)}</td>
                          <td className="py-1.5 text-right text-slate-500">{m.count}</td>
                          <td className="py-1.5 text-right text-slate-600">{eur(m.imponibile)}</td>
                          <td className="py-1.5 text-right text-slate-600">{eur(m.iva)}</td>
                          <td className="py-1.5 text-right font-semibold text-violet-700">{eur(m.totale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
          {!loading && data.partners.length === 0 && (
            <div className="py-14 text-center text-slate-400" data-testid="riv-empty">Nessun corso venduto da partner rivenditori nel periodo.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
